import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { useMemo } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import {
  FeatureResponse,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { FeatureType } from "src/clients/features-retrieve";
import {
  AndConditionInputTypeEnum,
  OrConditionInputTypeEnum,
} from "src/clients/jobs";
import { EntityFieldType, EntitySchemaResource } from "src/entities/queries";
import {
  buildEntityFieldOptions,
  EntityFieldOption,
} from "src/jobs/common/entitySource/EntityFieldsDropdown";
import { FilterCondition } from "src/jobs/common/entitySource/FilterCondition";
import { FieldErrors, FormData } from "src/jobs/common/entitySource/types";
import { buildClause } from "src/jobs/common/entitySource/utils";
import { assertUnreachable } from "src/utils/typeUtils";

export const JUNCTION_OPTIONS = [
  { key: AndConditionInputTypeEnum.AND, value: "and" },
  { key: OrConditionInputTypeEnum.OR, value: "or" },
];

type Props = {
  entitySchema: EntitySchemaResource;
  features: FeatureResponse[];
  fieldErrors: FieldErrors;
};

type ClauseGroupProps = {
  index: number;
  options: EntityFieldOption[];
  fieldTypes: Record<string, EntityFieldType>;
  fieldErrors: FieldErrors;
  mode: "where" | "junction";
  onRemove: (() => void) | undefined;
};

const ClauseGroup: React.FC<ClauseGroupProps> = ({
  index,
  options,
  fieldTypes,
  fieldErrors,
  mode,
  onRemove,
}) => {
  const {
    fields: clauses,
    remove,
    append,
  } = useFieldArray<FormData>({
    name: `filter_condition.conditions.${index}.conditions`,
  });

  return (
    <div className="flex gap-x-4">
      {onRemove && (
        <div className="basis-[72px] py-1.5">
          {mode === "where" && (
            <span className="text-gray-700 font-inter-medium-12px">Where</span>
          )}
          {mode === "junction" && (
            <Controller
              name="filter_condition.type"
              render={({ field }) => (
                <FixedSimpleDropdown
                  buttonDataLoc={`group-${index}-junction-button`}
                  dataLoc={`group-${index}-junction`}
                  disabled={index > 1}
                  elements={JUNCTION_OPTIONS}
                  placeholder="and"
                  selectedKey={field.value}
                  onSelect={field.onChange}
                />
              )}
            />
          )}
        </div>
      )}
      <div className="flex flex-grow flex-col gap-y-3 rounded-lg bg-white p-3 shadow-sm">
        {onRemove && (
          <div className="flex justify-end">
            <Button
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={onRemove}
            >
              Delete condition group
            </Button>
          </div>
        )}
        {clauses.map((clause, i) => (
          <FilterCondition
            key={clause.id}
            dataLoc={`group-${index}-filter-condition-${i}`}
            disabledJunction={i > 1}
            fieldErrors={fieldErrors}
            fieldTypes={fieldTypes}
            groupIndex={index}
            index={i}
            mode={i === 0 ? "where" : "junction"}
            options={options}
            onRemove={clauses.length > 1 ? () => remove(i) : undefined}
          />
        ))}
        <div>
          <Button
            dataLoc={`add-condition-${index}`}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => append(buildClause(options))}
          >
            Add condition
          </Button>
        </div>
      </div>
    </div>
  );
};

export const FilterConditionsAccordion = ({
  entitySchema,
  features,
  fieldErrors,
}: Props) => {
  const form = useFormContext<FormData>();

  const {
    fields: clauseGroups,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: "filter_condition.conditions",
  });

  const entityPropertyOptions = useMemo(
    () => buildEntityFieldOptions(entitySchema, features),
    [entitySchema, features],
  );

  const fieldTypes = useMemo(() => {
    const entityProperties = Object.entries(entitySchema.properties).reduce(
      (acc, [name, property]) => {
        acc[name] = property._type;
        return acc;
      },
      {} as Record<string, EntityFieldType>,
    );

    const entityFeatures = features.reduce(
      (acc, feature) => {
        acc[feature.key] = (() => {
          switch (feature.feature_type) {
            case FeatureType.BOOLEAN:
              return PropertyDefinitionOutputTypeEnum.BOOLEAN;
            case FeatureType.NUMBER:
              return PropertyDefinitionOutputTypeEnum.NUMBER;
            case FeatureType.STRING:
              return PropertyDefinitionOutputTypeEnum.STRING;
            case FeatureType.DATETIME:
              return PropertyDefinitionOutputTypeEnum.DATETIME;
            case FeatureType.DATE:
              return PropertyDefinitionOutputTypeEnum.DATE;
            case FeatureType.ARRAY:
              return PropertyDefinitionOutputTypeEnum.ARRAY;
            case FeatureType.OBJECT:
              return PropertyDefinitionOutputTypeEnum.OBJECT;
            default:
              assertUnreachable(feature.feature_type as never);
              return PropertyDefinitionOutputTypeEnum.STRING;
          }
        })();
        return acc;
      },
      {} as Record<string, EntityFieldType>,
    );

    return {
      ...entityProperties,
      ...entityFeatures,
      _updated_at: PropertyDefinitionOutputTypeEnum.DATETIME,
    };
  }, [entitySchema, features]);

  return (
    <div className="flex flex-col gap-y-3">
      {clauseGroups.map((group, i) => (
        <ClauseGroup
          key={group.id}
          fieldErrors={fieldErrors}
          fieldTypes={fieldTypes}
          index={i}
          mode={i === 0 ? "where" : "junction"}
          options={entityPropertyOptions}
          onRemove={clauseGroups.length > 1 ? () => remove(i) : undefined}
        />
      ))}
      <div>
        <Button
          dataLoc="add-condition-group"
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() =>
            append({
              type: AndConditionInputTypeEnum.AND,
              conditions: [buildClause(entityPropertyOptions)],
            })
          }
        >
          Add condition group
        </Button>
      </div>
    </div>
  );
};
